import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface RippleParams {
  amount: number;
  size: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Ripple: Filter<RippleParams> = {
  id: "ripple",
  displayName: "Ripple",
  defaultParams: { amount: 100, size: 10 },

  apply(input: PixelBuffer, params: RippleParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const cx = w / 2, cy = h / 2;
    const maxDist = Math.sqrt(cx * cx + cy * cy) || 1;
    const amplitude = params.amount * params.size / 100;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const dx = x - cx, dy = y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        const wave = Math.sin(dist * Math.PI * 2 / (params.size || 1)) * amplitude * (1 - dist / maxDist);
        const sx = Math.round(x + Math.cos(angle) * wave);
        const sy = Math.round(y + Math.sin(angle) * wave);

        const o = (y * w + x) * 4;
        if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
          const si = (sy * w + sx) * 4;
          dst[o] = src[si]!; dst[o + 1] = src[si + 1]!; dst[o + 2] = src[si + 2]!; dst[o + 3] = src[si + 3]!;
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
